import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DependencyList,
} from 'react';
import _ from 'lodash';

/**
 * A hook that allows you to run an arbitrary async function and get the data,
 * loading state, and error. "Reactifies" an arbitrary async function.
 *
 * @param asyncFunction - The async function to run.
 * @param params - The parameters to pass to the async function. Params should
 * preferrably be a small set of simple (ideally primitive) values, for
 * efficiency.
 * @param extraDeps - Additional dependencies besides for the params that may
 * require `asyncFunction` to be recalculated.
 * @returns A tuple containing the data, loading state, error, and a function to
 * run the async function.
 *
 * @example
 * const fetchData = async (a: number, b: number) => {
 *   const result = await fetch(`/api/data?a=${a}&b=${b}`);
 *   return result.json();
 * };
 *
 * const [data, isLoading, error, runFunction] = useAsyncData(
 *   fetchData,
 *   [param1, param2],
 *   [param1, param2],
 * );
 */
export const useAsyncData = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  FunctionType extends (...params: any[]) => Promise<any>,
>({
  asyncFunction,
  params,
  extraDeps = [],
}: {
  asyncFunction: FunctionType;
  params: Parameters<FunctionType>;
  extraDeps?: DependencyList;
}): [
  Awaited<ReturnType<FunctionType>>,
  boolean,
  Error | null,
  () => Promise<void>,
] => {
  const [data, setData] = useState<Awaited<ReturnType<FunctionType>> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // We use a ref to avoid re-creating the function and re-running the effect
  // every render
  const functionRef = useRef(asyncFunction);
  // We use an invocation count to help cancel previous invocations.
  const invocationCountRef = useRef(0);

  const stableParams = useStableParameters<Parameters<FunctionType>>(params);

  // Keep the function up to date (but non-reactive!)
  useEffect(() => {
    functionRef.current = asyncFunction;
  }, [asyncFunction]);

  // The actual function that can be called to run the async function
  const runFunction = useCallback(async () => {
    // The calledInvocationCount serves as an id for the current call to the
    // `runFunction`. If the current invocation count
    // (`invocationCountRef.current`) does not match the invocation count when
    // this exact invocation was called (`calledInvocationCount`), we should
    // ignore any updates to the state of our result variables.
    const calledInvocationCount = ++invocationCountRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await functionRef.current(...stableParams);
      if (calledInvocationCount === invocationCountRef.current) {
        setData(result);
      }
    } catch (error) {
      if (calledInvocationCount === invocationCountRef.current) {
        setError(error as Error);
      }
    } finally {
      if (calledInvocationCount === invocationCountRef.current) {
        setIsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [stableParams, ...extraDeps]);

  // Run the function when the params or deps change and on mount
  useEffect(() => {
    runFunction();

    // On unmount (i.e. if the run function or its dependencies change), we
    // increment the invocation count to cancel any in-flight invocations.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      invocationCountRef.current++;
    };
  }, [runFunction]);

  return [data, isLoading, error, runFunction];
};

/**
 * A hook that returns a deeply stable version of the parameters passed to
 * it. This hook enables the caller to provide simple inline parameters to the
 * hook without worrying about the parameters changing between renders.
 *
 * Since this test must run every render, this function should only be used with
 * a small number of simple (preferrably primitive) parameters.
 *
 * @param params - The parameters to return.
 * @returns The deeply stable parameters.
 */
 
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const useStableParameters = <T extends unknown>(params: T[]): T[] => {
  const paramsRef = useRef<T[]>(params);

  const outputParams: T[] = new Array<T>(params.length);
  let haveParametersChanged = paramsRef.current.length !== params.length;
  for (let i = 0; i < params.length; i++) {
    const oldValue = paramsRef.current[i];
    const newValue = params[i];

    if (_.isEqual(newValue, oldValue)) {
      outputParams[i] = oldValue;
    } else {
      haveParametersChanged = true;
      outputParams[i] = newValue;
    }
  }

  if (haveParametersChanged) {
    paramsRef.current = outputParams;
  }

  return paramsRef.current;
};

/**
 * A hook that detects when an element enters the viewport using IntersectionObserver.
 * Triggers once when the element first becomes visible and stays true afterwards.
 * Useful for triggering animations, lazy-loading content, or tracking viewport visibility.
 *
 * @param options - IntersectionObserver options (threshold, rootMargin, etc.)
 *   - threshold: Percentage of element that must be visible (0-1). Default: 0.3
 *   - rootMargin: Margin around the viewport for early/late triggering
 *   - root: Element to use as viewport (default: browser viewport)
 * @returns Object containing:
 *   - ref: Ref to attach to the element you want to observe
 *   - isInViewport: Boolean indicating if element has entered viewport
 *
 * @example
 * // Trigger animation when stats section is 30% visible
 * const {ref, isInViewport} = useInViewport({threshold: 0.3});
 *
 * return (
 *   <StatsSection ref={ref}>
 *     {isInViewport && <CountUpAnimation />}
 *   </StatsSection>
 * );
 *
 * @example
 * // Lazy load images with custom threshold
 * const {ref, isInViewport} = useInViewport({threshold: 0.1, rootMargin: '50px'});
 *
 * return (
 *   <div ref={ref}>
 *     {isInViewport && <img src={imageSrc} />}
 *   </div>
 * );
 */
export const useInViewport = (options: IntersectionObserverInit = {}) => {
  const [isInViewport, setIsInViewport] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return () => {/* do nothing */};

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          setIsInViewport(true);
        }
      },
      {threshold: 0.3, ...options},
    );

    observer.observe(element);
    return () => observer.disconnect();
    // Empty deps: Observer is created once and never recreated (one-time trigger by design).
    // Options captured from initial render, which is correct for this use case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {ref: elementRef, isInViewport};
};
